/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

'use client';

import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface Props {
  /**
   * ClassName to be applied to the main element
   */
  className?: string;
  /**
   * Children to be rendered inside the container
   */
  children?: ReactNode;
  /**
   * Display a gradient mask at the bottom of the container
   */
  mask?: boolean;
}

const PageScroll = ({ className = '', children, mask = true }: Props) => {
  const router = useRouter();
  const [isScrollMask, setScrollMask] = useState(true);
  const [isBottomScrolled, setBottomisScrollMask] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | undefined>(undefined);
  const prevScrollY = useRef<number>(0);
  const currentPageContent = useRef<HTMLElement | null>(null);

  function getScrollData() {
    if (typeof window !== 'undefined') {
      const htmlDocument = window.document.scrollingElement;
      if (!htmlDocument) {
        return;
      }
      const defaultHeight = 768;
      const bottomOffset = 40;
      const scrollOffset = htmlDocument.scrollTop || 0;
      const scrollHeight = htmlDocument.scrollHeight || defaultHeight;
      const clientHeight = htmlDocument.clientHeight || defaultHeight;
      const scrollOffsetMax = scrollHeight - clientHeight - bottomOffset;
      const contentOffset = currentPageContent.current?.offsetTop || 0;

      return {
        scrollOffset,
        scrollHeight,
        clientHeight,
        scrollOffsetMax,
        contentOffset,
      };
    }
  }

  function scrolledContentWatcher() {
    const pageContent = document.getElementById('pageContent');
    currentPageContent.current = pageContent || null;
    const scrollData = getScrollData();
    if (currentPageContent.current && scrollData) {
      if (scrollData.clientHeight + scrollData.scrollOffset >= scrollData.contentOffset) {
        setScrollMask(true);
      } else {
        setScrollMask(false);
      }
    } else {
      setScrollMask(true);
    }
  }

  // Scroll Listeners
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        const scrollData = getScrollData();
        if (!scrollData) {
          return;
        }

        // Lookup the scroll direction
        const currentY = scrollData.scrollOffset;
        const isScrollingDown = currentY > prevScrollY.current;
        const isOffsetCleared = currentY > 72;
        const isThresholdCleared = Math.abs(currentY - prevScrollY.current) > 6;

        if (isScrollingDown && isOffsetCleared  && isThresholdCleared) {
          setScrollDirection('down');
          document.body.setAttribute('data-visible-nav', 'false');
        } else if (currentY < prevScrollY.current) {
          setScrollDirection('up');
          document.body.setAttribute('data-visible-nav', 'true');
        }
        prevScrollY.current = currentY;

        // Lookup if page content (under the page header) is visible
        if (currentPageContent.current) {
          if (scrollData.clientHeight + scrollData.scrollOffset >= scrollData.contentOffset) {
            setScrollMask(true);
          } else {
            setScrollMask(false);
          }
        }

        // Lookup if scroll has reach the bottom of the page
        if (scrollData.scrollOffset >= scrollData.scrollOffsetMax) {
          setBottomisScrollMask(true);
        } else {
          setBottomisScrollMask(false);
        }
      };
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Scroll events on route change
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0 });
    }, 500);
    scrolledContentWatcher();
  }, [router]);

  // Scroll Mask
  // Display a gradient at the bottom of the app until bottom is reached
  const scrollMaskClasses = classNames(
    mask && [
      'before:z-10',
      'before:fixed',
      'before:bottom-0',
      'before:left-0',
      'before:h-16 lg:before:h-28',
      'before:w-full',
      'before:pointer-events-none',
      'before:bg-linear-to-t',
      'before:from-surface-black',
      'before:from-20%',
      'before:to-surface-dark/0',
      'before:transition-all',
      'before:ease-in-out',
      'before:duration-300',
      'before:opacity-0',
      'data-scroll-mask:before:opacity-100',
      'data-bottom:before:opacity-0',
      'data-auth:before:hidden lg:before:flex',
    ],
  );

  return (
    <div
      id='scrollContainer'
      className={classNames(
        'group/scroll-wrapper',
        'h-full',
        'flex flex-col justify-stretch',
        'relative',
        'text-white',
        scrollMaskClasses,
        className,
      )}
      data-scroll-mask={isScrollMask}
      data-bottom={isBottomScrolled}
      data-scroll-direction={scrollDirection}
    >
      {children}
    </div>
  );
};

export default PageScroll;
